import * as React from 'react';
import { Link } from 'react-router-dom';

import type { IExportCycle, IExportCycles } from '../../api/ranking-process-contracts';
import { LoadError } from '../../components/admin-page';
import { formatDay } from '../../components/days';
import { TEAM_PATHS } from '../../paths';
import { useRanking } from './ranking';

/**
 * The cycle a ranking's scoring input is written from and its scored result is linked to. **An
 * edition's cycle is fixed** (SR-451, RNK's "each ranking runs on its own cycle"): made with one
 * ("Start an edition…"), it is drawn as a fact on both tabs, and survey-api refuses an export
 * naming another (`cycle-fixed`) and a scored file of another cycle. Only an edition on none — one
 * migrated, or placed by hand — offers the choice (`chosenCycle` on the record, kept for the visit,
 * else the newest cycle with an as-of date), which its first export or import then fixes.
 *
 * The cycles are survey-api's (`GET …/export/cycles`), read once by the record.
 */
export interface ICycleChoice {
  /** Null while the read is out or failed. */
  cycles: Nullable<IExportCycles>;
  error: Nullable<string>;
  linked: Nullable<string>;
  /** The chosen cycle's code; null where no cycle carries the survey. */
  code: Nullable<string>;
  cycle: Nullable<IExportCycle>;
}

export function useCycleChoice(): ICycleChoice {
  const { exportCycles: cycles, exportCyclesError: error, chosenCycle } = useRanking();
  const linked = cycles?.linked ?? null;
  const dated = cycles?.cycles.find((entry) => entry.asOfDate !== null) ?? cycles?.cycles[0];
  const code = chosenCycle ?? linked ?? dated?.code ?? null;
  const cycle = cycles?.cycles.find((entry) => entry.code === code) ?? null;
  return { cycles, error, linked, code, cycle };
}

/** A cycle as the choice says it: its name, and the date its figures are read at. */
export function cycleWords(cycle: IExportCycle): string {
  if (cycle.asOfDate) return `${cycle.name} — figures as of ${formatDay(cycle.asOfDate)}`;
  return `${cycle.name} — no as-of date`;
}

/** The choice drawn, a select over the cycles pinning the survey with the linked one marked —
 *  worded for the tab it is on; `children` sit beside it (the tab's own press). */
export function CycleChoice({
  choice,
  tab,
  children
}: {
  choice: ICycleChoice;
  tab: 'export' | 'import';
  children?: React.ReactNode;
}) {
  const { chooseCycle } = useRanking();
  const { cycles, error, linked, code, cycle } = choice;
  if (cycles && linked !== null && cycle !== null) {
    return (
      <>
        {error && <LoadError message={error} />}
        <div className="flex flex-wrap items-end gap-3">
          <div className="grid gap-1 text-sm" data-export-cycle={linked} data-cycle-fixed="">
            <span className="text-muted-foreground">Cycle</span>
            <span className="flex h-9 items-center font-medium">{cycleWords(cycle)}</span>
          </div>
          {children}
        </div>
        {tab === 'export' && cycle.asOfDate === null && (
          <p className="text-sm text-muted-foreground" data-cycle-undated="">
            {cycle.name}’s pin has no as-of date, so its figures have no date to be read at. Set
            one on{' '}
            <Link className="underline" to={TEAM_PATHS.cycles}>
              Setup → Cycles
            </Link>
            .
          </p>
        )}
      </>
    );
  }
  const setup = (
    <Link className="underline" to={TEAM_PATHS.cycles}>
      Setup → Cycles
    </Link>
  );
  return (
    <>
      {error && <LoadError message={error} />}
      {cycles &&
        (cycles.cycles.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-export-cycle="none">
            {tab === 'export'
              ? 'No cycle carries this survey yet, so there is nothing to export from.'
              : 'No cycle carries this survey yet, so a file without a Cycle column has none to be linked to.'}{' '}
            Pin the survey to a cycle on {setup}.
          </p>
        ) : (
          <div className="flex flex-wrap items-end gap-3">
            <label className="grid gap-1 text-sm">
              <span className="text-muted-foreground">Cycle</span>
              <select
                aria-label="Cycle"
                data-export-cycle={code ?? ''}
                className="h-9 rounded-md border border-border bg-white px-2 text-sm"
                value={code ?? ''}
                onChange={(event) => chooseCycle(event.target.value)}
              >
                {cycles.cycles.map((entry) => (
                  <option key={entry.code} value={entry.code}>
                    {cycleWords(entry)}
                    {entry.code === linked ? ' (linked to this ranking)' : ''}
                  </option>
                ))}
              </select>
            </label>
            {children}
          </div>
        ))}
      {cycles && linked !== null && !cycles.cycles.some((entry) => entry.code === linked) && (
        <p className="text-sm text-muted-foreground" data-cycle-unpinned="">
          The ranking is linked to {linked}, which no longer carries this survey: its cycle is
          fixed, so pin the survey to {linked} again on {setup}.
        </p>
      )}
      {tab === 'export' && cycle && cycle.asOfDate === null && (
        <p className="text-sm text-muted-foreground" data-cycle-undated="">
          {cycle.name}’s pin has no as-of date, so its figures have no date to be read at. Set one
          on {setup}, or choose another cycle.
        </p>
      )}
    </>
  );
}
